/**
 * Brief Templates Admin Page
 *
 * CRUD for org-specific brief templates (platform templates are read-only).
 * Lives at /brief-templates under the Creative Production nav section.
 */

import { useEffect, useState } from 'react';
import {
  LayoutTemplate,
  Lock,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  ChevronRight,
  Building2,
  Globe,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeBriefTemplatesApi,
  type BriefTemplate,
} from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TemplateType =
  | 'product_launch'
  | 'retention'
  | 'dpa'
  | 'ugc'
  | 'seasonal'
  | 'custom';

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  product_launch: 'Product Launch',
  retention: 'Retention / LTV',
  dpa: 'DPA / Catalog',
  ugc: 'UGC Testimonial',
  seasonal: 'Seasonal',
  custom: 'Custom',
};

const BRIEF_SECTIONS = [
  { key: 'objective', label: 'Objective' },
  { key: 'audience', label: 'Audience' },
  { key: 'insight', label: 'Insight' },
  { key: 'brand_voice', label: 'Brand Voice' },
  { key: 'message', label: 'Message' },
  { key: 'creative_context', label: 'Creative Context' },
  { key: 'mandatories', label: 'Mandatories' },
  { key: 'success_metrics', label: 'Success Metrics' },
];

interface FormState {
  name: string;
  description: string;
  template_type: TemplateType;
  organization_id: string;
  prefill_sections: Record<string, string>; // key → JSON string
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  template_type: 'custom',
  organization_id: '',
  prefill_sections: {},
  is_active: true,
};

function parseFormPrefill(
  raw: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(raw)) {
    const trimmed = val.trim();
    if (!trimmed) continue;
    try {
      out[key] = JSON.parse(trimmed);
    } catch {
      out[key] = trimmed;
    }
  }
  return out;
}

function sectionJsonFromTemplate(t: BriefTemplate): Record<string, string> {
  const prefill = t.prefill_sections as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const { key } of BRIEF_SECTIONS) {
    const v = prefill[key];
    if (v !== undefined) {
      out[key] = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Platform badge / org badge
// ---------------------------------------------------------------------------

function ScopeBadge({ isPlatform }: { isPlatform: boolean }) {
  return isPlatform ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-sky-50 text-sky-700 border border-sky-200">
      <Globe size={9} />
      Platform
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-violet-50 text-violet-700 border border-violet-200">
      <Building2 size={9} />
      Org
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-slate-400 bg-slate-100 rounded">
      {type}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Template row (list)
// ---------------------------------------------------------------------------

function TemplateRow({
  template,
  selected,
  onClick,
}: {
  template: BriefTemplate;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors',
        selected && 'bg-brand-coral/5 border-l-2 border-l-brand-coral',
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
        {template.is_platform ? (
          <Globe size={14} className="text-sky-500" />
        ) : (
          <LayoutTemplate size={14} className="text-violet-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900 truncate">
            {template.name}
          </span>
          <TypeBadge type={template.template_type} />
          {!template.is_active && (
            <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded">
              Inactive
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
            {template.description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <ScopeBadge isPlatform={template.is_platform} />
        <ChevronRight size={14} className="text-slate-300" />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section prefill editor (one textarea per section)
// ---------------------------------------------------------------------------

function SectionEditor({
  values,
  onChange,
}: {
  values: Record<string, string>;
  onChange: (key: string, val: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
        <Info size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-blue-700 leading-snug">
          Each section accepts JSON or plain text. When a skill generates a
          brief using this template, empty sections are filled from here.
          Leave a section blank to let the LLM generate it freely.
        </p>
      </div>
      {BRIEF_SECTIONS.map(({ key, label }) => (
        <div key={key}>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            {label}
          </label>
          <textarea
            value={values[key] ?? ''}
            onChange={(e) => onChange(key, e.target.value)}
            rows={3}
            placeholder={`JSON or plain text for ${label} (leave empty to skip)`}
            className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 font-mono resize-none focus:outline-none focus:border-brand-coral placeholder:text-slate-300"
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit form modal
// ---------------------------------------------------------------------------

function TemplateFormModal({
  initial,
  onSave,
  onClose,
  saving,
  error,
}: {
  initial: FormState;
  onSave: (form: FormState) => void;
  onClose: () => void;
  saving: boolean;
  error: string | null;
}) {
  const [form, setForm] = useState<FormState>(initial);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const isValid = form.name.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <LayoutTemplate size={16} className="text-brand-coral" />
            <h2 className="text-sm font-bold text-slate-900">
              {initial.name ? 'Edit Template' : 'New Brief Template'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Name <span className="text-rose-400">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Holiday Campaign"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-brand-coral"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="What this template is for…"
              className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 resize-none focus:outline-none focus:border-brand-coral"
            />
          </div>

          {/* Type + Org row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Template Type
              </label>
              <select
                value={form.template_type}
                onChange={(e) =>
                  set('template_type', e.target.value as TemplateType)
                }
                className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:border-brand-coral"
              >
                {Object.entries(TEMPLATE_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Organization ID
              </label>
              <input
                value={form.organization_id}
                onChange={(e) => set('organization_id', e.target.value)}
                placeholder="e.g. skoutorganic"
                className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-brand-coral"
              />
            </div>
          </div>

          {/* Active toggle (only in edit mode) */}
          {initial.name && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('is_active', !form.is_active)}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                {form.is_active ? (
                  <ToggleRight size={20} className="text-emerald-500" />
                ) : (
                  <ToggleLeft size={20} className="text-slate-300" />
                )}
                {form.is_active ? 'Active' : 'Inactive'}
              </button>
              <span className="text-[11px] text-slate-400">
                Inactive templates are hidden from the new-brief picker
              </span>
            </div>
          )}

          {/* Prefill sections */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
              Pre-fill Sections
            </div>
            <SectionEditor
              values={form.prefill_sections}
              onChange={(key, val) =>
                setForm((f) => ({
                  ...f,
                  prefill_sections: { ...f.prefill_sections, [key]: val },
                }))
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50">
          {error && (
            <div className="flex items-center gap-1.5 text-xs text-rose-600">
              <AlertCircle size={13} />
              {error}
            </div>
          )}
          {!error && <div />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(form)}
              disabled={!isValid || saving}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-semibold text-white bg-brand-coral rounded-lg hover:bg-brand-coral/90 disabled:opacity-50"
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {initial.name ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel (right side)
// ---------------------------------------------------------------------------

function DetailPanel({
  template,
  onEdit,
  onDelete,
  onClose,
  deleting,
}: {
  template: BriefTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  deleting: boolean;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const prefill = template.prefill_sections as Record<string, unknown>;
  const filledSections = BRIEF_SECTIONS.filter(
    ({ key }) => prefill[key] !== undefined,
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ScopeBadge isPlatform={template.is_platform} />
            <TypeBadge type={template.template_type} />
            {!template.is_active && (
              <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded">
                Inactive
              </span>
            )}
          </div>
          <h2 className="text-base font-bold text-slate-900 leading-snug">
            {template.name}
          </h2>
          <code className="text-[10px] text-slate-400">
            {template.template_id}
          </code>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!template.is_platform && (
            <>
              <button
                onClick={onEdit}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-brand-coral transition-colors"
                title="Edit template"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-rose-500 transition-colors"
                title="Delete template"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          {template.is_platform && (
            <span
              className="p-1.5 text-slate-300"
              title="Platform templates are read-only"
            >
              <Lock size={14} />
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="mx-4 mt-4 border border-rose-200 bg-rose-50 rounded-xl px-4 py-3">
          <p className="text-xs text-rose-700 font-medium mb-3">
            Delete "{template.name}"? This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-rose-500 rounded-lg hover:bg-rose-600 disabled:opacity-50"
            >
              {deleting && <Loader2 size={11} className="animate-spin" />}
              Delete
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* Description */}
        {template.description && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Description
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {template.description}
            </p>
          </div>
        )}

        {/* Org */}
        {template.organization_id && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
              Organization
            </div>
            <code className="text-xs text-slate-700 bg-slate-100 px-2 py-1 rounded">
              {template.organization_id}
            </code>
          </div>
        )}

        {/* Platform read-only callout */}
        {template.is_platform && (
          <div className="flex gap-2 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5">
            <Lock size={13} className="text-sky-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-sky-700 leading-snug">
              Platform templates are provided by NeuroGraph and cannot be
              edited or deleted. Create an org-specific template to customise
              the pre-fill content for your team.
            </p>
          </div>
        )}

        {/* Pre-fill sections */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">
            Pre-fill Sections
            <span className="ml-2 text-[10px] font-normal normal-case text-slate-400">
              {filledSections.length} of {BRIEF_SECTIONS.length} configured
            </span>
          </div>
          {filledSections.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              No sections pre-filled — all sections will be LLM-generated.
            </p>
          ) : (
            <div className="space-y-3">
              {filledSections.map(({ key, label }) => {
                const val = prefill[key];
                const display =
                  typeof val === 'object'
                    ? JSON.stringify(val, null, 2)
                    : String(val);
                return (
                  <div key={key}>
                    <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      <Check size={10} className="text-emerald-500" />
                      {label}
                    </div>
                    <pre className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed">
                      {display}
                    </pre>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Channel directives */}
        {template.default_channel_directives.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Default Channel Directives
            </div>
            <div className="space-y-2">
              {template.default_channel_directives.map((d, i) => {
                const dir = d as Record<string, unknown>;
                return (
                  <div
                    key={i}
                    className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                  >
                    <div className="text-xs font-semibold text-slate-700 capitalize mb-0.5">
                      {String(dir.channel ?? '—')}
                    </div>
                    {!!dir.notes && (
                      <p className="text-[11px] text-slate-500">
                        {String(dir.notes)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Timestamps */}
        {(template.created_at || template.updated_at) && (
          <div className="text-[10px] text-slate-400 space-y-0.5 pt-2 border-t border-slate-100">
            {template.created_at && (
              <div>
                Created{' '}
                {new Date(template.created_at).toLocaleDateString(undefined, {
                  dateStyle: 'medium',
                })}
              </div>
            )}
            {template.updated_at && (
              <div>
                Updated{' '}
                {new Date(template.updated_at).toLocaleDateString(undefined, {
                  dateStyle: 'medium',
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function BriefTemplatesPage() {
  const [templates, setTemplates] = useState<BriefTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<BriefTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formInitial, setFormInitial] = useState<FormState>(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const platform = templates.filter((t) => t.is_platform);
  const org = templates.filter((t) => !t.is_platform);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await creativeBriefTemplatesApi.list(
        undefined,
        true /* include inactive */,
      );
      setTemplates(res.templates ?? []);
    } catch (err) {
      setLoadError((err as Error).message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setFormInitial(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(t: BriefTemplate) {
    setFormInitial({
      name: t.name,
      description: t.description ?? '',
      template_type: t.template_type as TemplateType,
      organization_id: t.organization_id ?? '',
      prefill_sections: sectionJsonFromTemplate(t),
      is_active: t.is_active,
    });
    setFormError(null);
    setShowForm(true);
  }

  async function handleSave(form: FormState) {
    setFormSaving(true);
    setFormError(null);
    const prefillParsed = parseFormPrefill(form.prefill_sections);
    try {
      if (formInitial.name) {
        // Edit mode — find by matching name in selected
        if (!selected) return;
        await creativeBriefTemplatesApi.update(selected.template_id, {
          name: form.name,
          description: form.description,
          template_type: form.template_type,
          prefill_sections: prefillParsed,
          is_active: form.is_active,
        });
      } else {
        // Create mode
        await creativeBriefTemplatesApi.create({
          name: form.name,
          description: form.description,
          template_type: form.template_type,
          prefill_sections: prefillParsed,
          organization_id: form.organization_id || undefined,
        });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError((err as Error).message || 'Save failed');
    } finally {
      setFormSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected || selected.is_platform) return;
    setDeleting(true);
    try {
      await creativeBriefTemplatesApi.delete(selected.template_id);
      setSelected(null);
      await load();
    } catch (err) {
      // Surface error in detail panel header
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <LayoutTemplate size={22} className="text-brand-coral" />
            Brief Templates
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Platform templates are read-only. Create org-specific templates to
            pre-fill brief sections and channel directives for your team.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-coral rounded-xl hover:bg-brand-coral/90 transition-colors"
        >
          <Plus size={15} />
          New Template
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          {
            label: 'Platform Templates',
            value: platform.length,
            note: 'Read-only',
            color: 'sky',
          },
          {
            label: 'Org Templates',
            value: org.length,
            note: 'Customisable',
            color: 'violet',
          },
          {
            label: 'Active',
            value: templates.filter((t) => t.is_active).length,
            note: 'Shown in picker',
            color: 'emerald',
          },
        ].map(({ label, value, note, color }) => (
          <div
            key={label}
            className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3"
          >
            <div
              className={clsx(
                'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                color === 'sky' && 'bg-sky-50',
                color === 'violet' && 'bg-violet-50',
                color === 'emerald' && 'bg-emerald-50',
              )}
            >
              <LayoutTemplate
                size={16}
                className={clsx(
                  color === 'sky' && 'text-sky-500',
                  color === 'violet' && 'text-violet-500',
                  color === 'emerald' && 'text-emerald-500',
                )}
              />
            </div>
            <div>
              <div className="text-xl font-bold text-navy-900 leading-none">
                {value}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
              <div className="text-[9px] text-slate-400">{note}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* List */}
        <div
          className={clsx(
            'bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col transition-all',
            selected ? 'w-[420px] flex-shrink-0' : 'flex-1',
          )}
        >
          {/* List header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">
              All Templates
            </span>
            <span className="text-[10px] text-slate-400">
              {templates.length} total
            </span>
          </div>

          {/* List body */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading templates…</span>
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2 mx-4 my-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                <AlertCircle size={14} />
                {loadError}
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                <LayoutTemplate size={36} className="text-slate-200 mb-3" />
                <p className="text-sm font-medium text-slate-500">
                  No templates yet
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Create an org-specific template to get started.
                </p>
              </div>
            ) : (
              <>
                {platform.length > 0 && (
                  <div>
                    <div className="px-4 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Platform Templates
                    </div>
                    {platform.map((t) => (
                      <TemplateRow
                        key={t.template_id}
                        template={t}
                        selected={selected?.template_id === t.template_id}
                        onClick={() =>
                          setSelected(
                            selected?.template_id === t.template_id
                              ? null
                              : t,
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                {org.length > 0 && (
                  <div>
                    <div className="px-4 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Organization Templates
                    </div>
                    {org.map((t) => (
                      <TemplateRow
                        key={t.template_id}
                        template={t}
                        selected={selected?.template_id === t.template_id}
                        onClick={() =>
                          setSelected(
                            selected?.template_id === t.template_id
                              ? null
                              : t,
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
            <DetailPanel
              template={selected}
              onEdit={() => openEdit(selected)}
              onDelete={handleDelete}
              onClose={() => setSelected(null)}
              deleting={deleting}
            />
          </div>
        )}
      </div>

      {/* Create / edit modal */}
      {showForm && (
        <TemplateFormModal
          initial={formInitial}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
          saving={formSaving}
          error={formError}
        />
      )}
    </div>
  );
}
